import { useState } from "react";
import { Play, BookOpen, CircleHelp } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { QuickGuide } from "@/components/quick-guide/QuickGuide";
import VideoPlayer from "@/components/support/VideoPlayer";
import { HOW_IT_WORKS_HERO_VIDEO_URL } from "@/config/constants";
import { useLanguage } from "@/context/LanguageContext";
import { authLabels, commonLabels, dashboardLabels } from "@/i18n";

/**
 * Native /auth — intro sheet below “Skapa konto”: how-it-works video + quick guide.
 */
const NativeAuthOnboarding = () => {
  const { language } = useLanguage();
  const auth = authLabels(language);
  const onboarding = auth.nativeOnboarding ?? {};
  const common = commonLabels(language);
  const dash = dashboardLabels(language);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);

  const videoTitle =
    common.menuPages?.howItWorks?.hero?.videoModalTitle ?? onboarding.howItWorks;
  const guideTitle = dash.quickGuide?.title ?? "Snabb guide";

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        className="native-auth-discover-btn w-full min-h-11 h-11 text-[15px] font-medium inline-flex flex-row items-center justify-center gap-2 rounded-lg border-0 bg-transparent text-muted-foreground shadow-none hover:bg-muted/30 hover:text-foreground active:scale-[0.99] transition-all"
        onClick={() => setSheetOpen(true)}
      >
        <CircleHelp className="h-4 w-4 shrink-0 opacity-80" strokeWidth={2} />
        <span className="leading-tight">
          {onboarding.cta ?? "Vad är BILENIA?"}
        </span>
      </Button>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="bottom">
          <SheetHeader className="text-left">
            <SheetTitle>{onboarding.sheetTitle}</SheetTitle>
            <SheetDescription>{onboarding.sheetDescription}</SheetDescription>
          </SheetHeader>
          <div className="mt-6 flex flex-col gap-3">
            <Button
              type="button"
              className="w-full min-h-14 h-14 justify-start gap-3 text-base font-semibold"
              onClick={() => {
                setSheetOpen(false);
                setVideoOpen(true);
              }}
            >
              <Play className="h-5 w-5 shrink-0" />
              {onboarding.howItWorks}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full min-h-14 h-14 justify-start gap-3 text-base font-semibold border-2"
              onClick={() => {
                setSheetOpen(false);
                setGuideOpen(true);
              }}
            >
              <BookOpen className="h-5 w-5 shrink-0" />
              {guideTitle}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {videoOpen && (
        <VideoPlayer
          videoUrl={HOW_IT_WORKS_HERO_VIDEO_URL}
          title={videoTitle}
          onClose={() => setVideoOpen(false)}
          defaultPlaybackRate={0.75}
        />
      )}

      <QuickGuide open={guideOpen} onOpenChange={setGuideOpen} showQuickGuide={false} />
    </>
  );
};

export default NativeAuthOnboarding;
